import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  query,
  where,
  writeBatch,
  serverTimestamp,
  type Unsubscribe,
  type WriteBatch,
} from '@firebase/firestore';
import { db } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import {
  DefensePlanAssignment,
  PlanParty,
  PlanPartyContact,
  PlanPartyType,
  PlanAssignmentStatus,
} from '../types';
import { mapDoc, mapDocs } from '@utils/firestore';
import {
  DefensePlanAssignmentSchema,
  PlanPartySchema,
  PlanPartyContactSchema,
} from '../schemas/firestoreSchemas';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('api:defensePlanParties');

function partiesCollection(planId: string) {
  return collection(db, COLLECTIONS.DEFENSE_PLANS, planId, COLLECTIONS.DEFENSE_PLAN_PARTIES);
}
function partyDoc(planId: string, partyId: string) {
  return doc(db, COLLECTIONS.DEFENSE_PLANS, planId, COLLECTIONS.DEFENSE_PLAN_PARTIES, partyId);
}
function contactDoc(planId: string, partyId: string) {
  return doc(
    db,
    COLLECTIONS.DEFENSE_PLANS,
    planId,
    COLLECTIONS.DEFENSE_PLAN_PARTY_CONTACTS,
    partyId
  );
}
function partyContactsCollection(planId: string) {
  return collection(db, COLLECTIONS.DEFENSE_PLANS, planId, COLLECTIONS.DEFENSE_PLAN_PARTY_CONTACTS);
}
function assignmentsCollection(planId: string) {
  return collection(db, COLLECTIONS.DEFENSE_PLANS, planId, COLLECTIONS.DEFENSE_PLAN_ASSIGNMENTS);
}
function assignmentDoc(planId: string, assignmentId: string) {
  return doc(
    db,
    COLLECTIONS.DEFENSE_PLANS,
    planId,
    COLLECTIONS.DEFENSE_PLAN_ASSIGNMENTS,
    assignmentId
  );
}
// The assignment doc id is deterministic — a (feature, party) pair has at
// most one assignment, checked server-side by the rules' own id equality
// test, not trusted from the client alone.
export function assignmentId(featureId: string, partyId: string): string {
  return `${featureId}__${partyId}`;
}

// Firestore rejects undefined values — organization/person/uid/description/
// email are all optional on the schemas.
function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

export interface PartyInput {
  type: PlanPartyType;
  organization?: string;
  person?: string;
  uid?: string;
}

export const defensePlanPartiesApi = {
  // A plan's whole parties collection is small (there's no cap today, but
  // in practice a handful to a few dozen), so this is the one read shape —
  // no paging, keyed by id for O(1) row lookups.
  subscribeParties(
    planId: string,
    onNext: (parties: Record<string, PlanParty>) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      partiesCollection(planId),
      (snap) => {
        const list = mapDocs(snap, PlanPartySchema);
        onNext(Object.fromEntries(list.map((p) => [p.id, p])));
      },
      (error) => {
        // A plan link to a private or deleted plan is denied by design and
        // the viewer already says the plan isn't available; warn, don't
        // error (the dev server turns console.error into a red toast).
        if ((error as { code?: string }).code === 'permission-denied') {
          log.warn('subscribeParties denied:', error);
        } else {
          log.error('subscribeParties failed:', error);
        }
        onError?.(error);
      }
    );
  },

  subscribeAssignments(
    planId: string,
    onNext: (assignments: Record<string, DefensePlanAssignment>) => void,
    onError?: (error: Error) => void
  ): Unsubscribe {
    return onSnapshotWithRetry(
      assignmentsCollection(planId),
      (snap) => {
        const list = mapDocs(snap, DefensePlanAssignmentSchema);
        onNext(Object.fromEntries(list.map((a) => [a.id, a])));
      },
      (error) => {
        // A plan link to a private or deleted plan is denied by design and
        // the viewer already says the plan isn't available; warn, don't
        // error (the dev server turns console.error into a red toast).
        if ((error as { code?: string }).code === 'permission-denied') {
          log.warn('subscribeAssignments denied:', error);
        } else {
          log.error('subscribeAssignments failed:', error);
        }
        onError?.(error);
      }
    );
  },

  // One party, for the public event page's "led by" line. Null when the plan
  // is private (rules deny the read) or the party is gone; the caller shows
  // nothing in either case.
  async getParty(planId: string, partyId: string): Promise<PlanParty | null> {
    try {
      return mapDoc(await getDoc(partyDoc(planId, partyId)), PlanPartySchema);
    } catch (err) {
      if ((err as { code?: string })?.code === 'permission-denied') return null;
      throw err;
    }
  },

  // Pre-generates a party id the same way defensePlansApi.newPlanId does —
  // lets the store build an optimistic party before the write lands.
  newPartyId(planId: string): string {
    return doc(partiesCollection(planId)).id;
  },

  async createParty(
    planId: string,
    partyId: string,
    data: PartyInput,
    createdBy: string
  ): Promise<void> {
    await setDoc(
      partyDoc(planId, partyId),
      stripUndefined({
        type: data.type,
        organization: data.organization,
        person: data.person,
        uid: data.uid,
        createdBy,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
    );
  },

  async updateParty(
    planId: string,
    partyId: string,
    data: { type: PlanPartyType; organization?: string; person?: string }
  ): Promise<void> {
    // The caller passes '' (never undefined) for a field it wants cleared —
    // deleteField() actually removes it via updateDoc, where a bare
    // `undefined` would throw client-side.
    await updateDoc(partyDoc(planId, partyId), {
      type: data.type,
      ...(data.organization !== undefined
        ? { organization: data.organization || deleteField() }
        : {}),
      ...(data.person !== undefined ? { person: data.person || deleteField() } : {}),
      updatedAt: serverTimestamp(),
    });
  },

  // Cascading delete: the rules can't do this server-side, so the client
  // deletes the party, its contact, and every assignment that links to it,
  // all in one batch. Firestore's 500-writes-per-batch cap is nowhere near
  // a real plan's assignment count for one party.
  async deleteParty(planId: string, partyId: string): Promise<void> {
    const linked = await getDocs(
      query(assignmentsCollection(planId), where('partyId', '==', partyId))
    );
    const batch = writeBatch(db);
    batch.delete(partyDoc(planId, partyId));
    batch.delete(contactDoc(planId, partyId));
    for (const d of linked.docs) batch.delete(d.ref);
    await batch.commit();
  },

  async getPartyContact(planId: string, partyId: string): Promise<PlanPartyContact | null> {
    try {
      return mapDoc(await getDoc(contactDoc(planId, partyId)), PlanPartyContactSchema);
    } catch (err) {
      // Not every viewer can read a contact (owner-only) — treat denial as
      // "no contact to show" rather than surfacing a Firestore error.
      if ((err as { code?: string })?.code === 'permission-denied') return null;
      throw err;
    }
  },

  async setPartyContact(planId: string, partyId: string, email: string | undefined): Promise<void> {
    await setDoc(
      contactDoc(planId, partyId),
      stripUndefined({ email, updatedAt: serverTimestamp() })
    );
  },

  // Owner create-or-update, upsert on the doc's existence — same shape as
  // defensePlansApi.createPlan's idempotent-create dance. `source`/
  // `createdBy`/`createdAt` are set only on create and never touched on
  // update, matching the rules' frozen-field check.
  async setOwnerAssignment(
    planId: string,
    featureId: string,
    partyId: string,
    data: { description?: string; status: PlanAssignmentStatus },
    ownerUid: string
  ): Promise<void> {
    const id = assignmentId(featureId, partyId);
    const ref = assignmentDoc(planId, id);
    let exists = false;
    try {
      exists = (await getDoc(ref)).exists();
    } catch (err) {
      if ((err as { code?: string })?.code !== 'permission-denied') throw err;
    }
    if (!exists) {
      await setDoc(
        ref,
        stripUndefined({
          featureId,
          partyId,
          description: data.description,
          status: data.status,
          source: 'owner' as const,
          createdBy: ownerUid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      );
    } else {
      await updateDoc(
        ref,
        stripUndefined({
          description: data.description ?? '',
          status: data.status,
          updatedAt: serverTimestamp(),
        })
      );
    }
  },

  // A signed-in homeowner linking their own party to a feature.
  async createVolunteerAssignment(
    planId: string,
    featureId: string,
    partyId: string,
    uid: string
  ): Promise<void> {
    const id = assignmentId(featureId, partyId);
    await setDoc(assignmentDoc(planId, id), {
      featureId,
      partyId,
      status: 'accepted' as const,
      source: 'volunteer' as const,
      createdBy: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },

  async updateAssignmentStatus(
    planId: string,
    assignmentDocId: string,
    status: PlanAssignmentStatus
  ): Promise<void> {
    await updateDoc(assignmentDoc(planId, assignmentDocId), {
      status,
      updatedAt: serverTimestamp(),
    });
  },

  async deleteAssignment(planId: string, assignmentDocId: string): Promise<void> {
    await deleteDoc(assignmentDoc(planId, assignmentDocId));
  },

  // Cascading delete for the whole plan (Sept 17 2026): every party, its
  // contact (partyContacts can hold a private email — see the schema/rules
  // comment on that collection), and every assignment. Queued onto the SAME
  // batch as the plan doc's own delete — see defensePlansApi.deletePlan,
  // the only caller — so the delete is all-or-nothing, and named "queue"
  // rather than "delete" since it only stages work on the batch it's given;
  // the caller still owns commit(). The rules' isPlanOwner()/isPlanShared()
  // checks for these subcollections do a get() on the parent plan doc, and
  // Firestore evaluates every operation in a batch against the state as it
  // stood BEFORE the batch — none of a batch's own writes are visible to
  // another operation's rule check within it — so those checks still see
  // the (not-yet-deleted) plan doc here, the same reasoning
  // deleteParty's own party+contact+assignments batch already relies on.
  async queueDeleteAllForPlan(batch: WriteBatch, planId: string): Promise<void> {
    const [parties, contacts, assignments] = await Promise.all([
      getDocs(partiesCollection(planId)),
      getDocs(partyContactsCollection(planId)),
      getDocs(assignmentsCollection(planId)),
    ]);
    for (const d of parties.docs) batch.delete(d.ref);
    for (const d of contacts.docs) batch.delete(d.ref);
    for (const d of assignments.docs) batch.delete(d.ref);
  },
};
